import { ChevronIcon, DatabaseIcon } from "./Icons";
import { formatPrimitive, humanizeKey } from "../lib/formatters";
import type { JsonObject, JsonValue } from "../types/json";

interface DataExplorerProps {
  data: JsonObject;
}

function PrimitiveRow({ label, fieldKey, value }: { label: string; fieldKey: string; value: JsonValue }) {
  const primitive = value as string | number | boolean | null;
  const missing = primitive === null || primitive === "";

  return (
    <div className="grid gap-1 border-b border-[#e8eef5] px-3 py-3 last:border-b-0 sm:grid-cols-[minmax(180px,0.72fr)_1.28fr] sm:gap-5 sm:px-4">
      <dt className="break-words text-xs font-medium text-[#6f7f94]">{label}</dt>
      <dd className={`break-words text-sm ${missing ? "italic text-[#9aa7b6]" : "text-[#17253a]"}`}>
        {formatPrimitive(fieldKey, primitive)}
      </dd>
    </div>
  );
}

function JsonNode({ label, fieldKey, value, depth }: { label: string; fieldKey: string; value: JsonValue; depth: number }) {
  if (value === null || typeof value !== "object") {
    return <PrimitiveRow label={label} fieldKey={fieldKey} value={value} />;
  }

  if (Array.isArray(value)) {
    return (
      <details className="data-group" open={depth === 0}>
        <summary className="data-summary">
          <span className="flex min-w-0 items-center gap-2">
            <ChevronIcon className="chevron size-4 shrink-0 text-[#6f7f94]" />
            <span className="truncate font-medium text-[#17253a]">{label}</span>
          </span>
          <span className="rounded-full bg-[#eef3f8] px-2.5 py-1 text-[11px] text-[#6f7f94]">
            {value.length} {value.length === 1 ? "item" : "itens"}
          </span>
        </summary>
        <div className="space-y-2 p-2 sm:p-3">
          {value.length === 0 ? (
            <p className="px-3 py-4 text-sm italic text-[#9aa7b6]">Lista vazia</p>
          ) : (
            value.map((item, index) => (
              <div key={`${fieldKey}-${index}`} className="overflow-hidden rounded-xl border border-[#dce5ef] bg-[#f8fafd]">
                <JsonNode
                  label={`Item ${index + 1}`}
                  fieldKey={`${fieldKey}[${index}]`}
                  value={item}
                  depth={depth + 1}
                />
              </div>
            ))
          )}
        </div>
      </details>
    );
  }

  const entries = Object.entries(value);
  return (
    <details className="data-group" open={depth === 0}>
      <summary className="data-summary">
        <span className="flex min-w-0 items-center gap-2">
          <ChevronIcon className="chevron size-4 shrink-0 text-[#6f7f94]" />
          <span className="truncate font-medium text-[#17253a]">{label}</span>
        </span>
        <span className="rounded-full bg-[#eef3f8] px-2.5 py-1 text-[11px] text-[#6f7f94]">
          {entries.length} {entries.length === 1 ? "campo" : "campos"}
        </span>
      </summary>
      <dl className="m-2 overflow-hidden rounded-xl border border-[#e8eef5] bg-white sm:m-3">
        {entries.length === 0 ? (
          <p className="px-4 py-4 text-sm italic text-[#9aa7b6]">Objeto vazio</p>
        ) : (
          entries.map(([key, childValue]) => (
            <JsonNode
              key={`${fieldKey}.${key}`}
              label={humanizeKey(key)}
              fieldKey={key}
              value={childValue}
              depth={depth + 1}
            />
          ))
        )}
      </dl>
    </details>
  );
}

export function DataExplorer({ data }: DataExplorerProps) {
  return (
    <section className="card rounded-[2rem] p-4 sm:p-6" aria-labelledby="all-data-title">
      <div className="mb-5 flex items-start gap-3 px-1">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-[#eaf2fa] text-[#1c5a95] ring-1 ring-inset ring-[#c9dbeb]">
          <DatabaseIcon className="size-5" />
        </div>
        <div>
          <h2 id="all-data-title" className="text-lg font-semibold text-[#17253a]">
            Todos os dados retornados
          </h2>
          <p className="mt-1 text-sm leading-6 text-[#6f7f94]">
            Objetos e listas são organizados automaticamente em uma árvore expansível.
          </p>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-[#dce5ef] bg-[#f8fafd]">
        <JsonNode label="Resposta da API" fieldKey="root" value={data} depth={0} />
      </div>
    </section>
  );
}
